import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"
import { showConfirmModal } from "../lib/custom_confirm"

// The shared strip's navigating tab links (shared/_tab_strip): real anchors
// carrying `?tab=`. A locked tab is a <button> with no href that opens the
// locked-feature modal instead, so it never matches.
const TAB_LINK_SELECTOR = ".gl-page-action-strip__tabs a.gl-haml-tab[href]"

// The unsaved-changes confirmation the matrix already established (copy,
// cautionary tier, button label) — the ONE styled prompt this page shows.
const UNSAVED_MESSAGE = "You have unsaved changes. Discard them and leave?"
const UNSAVED_TITLE = "Unsaved changes"
const LEAVE_LABEL = "Discard & Leave"

// Connects to data-controller="integration-fields-matrix"
//
// Mounted on the Integration Fields page's standard_page_shell ROOT (not the
// matrix card): the shared tab strip is the shell's `page.tabs` slot, a
// SIBLING of the card, and a Stimulus controller only sees events inside its
// own element — so a guard on the card never saw a tab click and dirty edits
// were lost on every in-app tab change (#14377). The root also carries
// `data-action="click->integration-fields-matrix#guardTabNavigation"`, the
// canonical Stimulus binding for the strip's links: Stimulus removes it on
// disconnect and rebinds on every Turbo render, so reconnects never stack a
// second handler or a second prompt.
export default class extends Controller {
  static targets = ["row", "field", "saveBar", "count"]
  static values = { batchUrl: String }

  connect() {
    this.fieldTargets.forEach((field) => {
      field.dataset.baseline = this.#asBaselineString(field)
    })

    this.#updateSaveBar()

    this.#boundBeforeUnload = this.beforeUnload.bind(this)
    window.addEventListener("beforeunload", this.#boundBeforeUnload)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.#boundBeforeUnload)
    this.#leaving = false
  }

  filterRows(event) {
    const query = event.currentTarget.value.trim().toLowerCase()

    this.rowTargets.forEach((row) => {
      const haystack = row.dataset.search || ""
      row.classList.toggle("d-none", query.length > 0 && !haystack.includes(query))
    })
  }

  markDirty() {
    this.fieldTargets.forEach((field) => {
      field.classList.toggle("is-dirty", this.#isDirty(field))
    })

    this.#updateSaveBar()
  }

  discard() {
    this.fieldTargets.forEach((field) => {
      const baseline = field.dataset.baseline
      if (field.type === "checkbox") {
        field.checked = baseline === "true"
      } else {
        field.value = baseline
      }
    })

    this.markDirty()
  }

  async save() {
    const dirty = this.dirtyFields()
    if (dirty.length === 0) return
    if (this.#saving) return
    this.#saving = true

    const changesMap = new Map()
    dirty.forEach((field) => {
      const key = `${field.dataset.resource}:${field.dataset.id}`
      if (!changesMap.has(key)) {
        changesMap.set(key, {
          resource: field.dataset.resource,
          id: field.dataset.id,
          attributes: {}
        })
      }
      changesMap.get(key).attributes[field.dataset.attribute] = this.#valueOf(field)
    })

    const changes = Array.from(changesMap.values())

    this.#setSaveBarButtonsDisabled(true)

    try {
      const response = await patch(this.batchUrlValue, {
        body: JSON.stringify({ changes }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        dirty.forEach((field) => {
          field.dataset.baseline = this.#asBaselineString(field)
        })
        this.markDirty()
        this.#toast("Saved", "success")
      } else {
        const error = await response.json
        this.#toast(error.error || "Failed to save changes", "danger")
      }
    } catch {
      // Network failure or a non-JSON error body (e.g. a 502 HTML page). Keep the
      // edits staged (dirty state is untouched) and surface feedback to the user.
      this.#toast("Failed to save changes", "danger")
    } finally {
      this.#setSaveBarButtonsDisabled(false)
      this.#saving = false
    }
  }

  bulk(event) {
    const button = event.currentTarget
    const column = button.dataset.column
    const value = button.dataset.value === "true"

    if (!column) return

    this.fieldTargets.forEach((field) => {
      if (field.type !== "checkbox") return
      if (field.dataset.attribute !== column) return
      if (field.classList.contains("d-none")) return

      const row = field.closest("[data-integration-fields-matrix-target='row']")
      if (row && row.classList.contains("d-none")) return

      field.checked = value
    })

    this.markDirty()
  }

  // Browser-level protection (reload, close, address bar): the only prompt a
  // browser allows here is its native one, so this stays alongside the styled
  // in-app guard below rather than replacing it.
  beforeUnload(event) {
    if (this.dirtyFields().length === 0) return
    event.preventDefault()
    event.returnValue = ""
  }

  // The in-app guard: a click anywhere in the root that resolves to one of the
  // strip's tab links (a keyboard Enter on the focused link arrives as the
  // same click) opens the styled confirmation while edits are pending. Cancel
  // leaves the URL, the edits and the dirty state exactly as they were;
  // Discard & Leave resets the edits and navigates exactly once. A clean page
  // lets the link navigate on its own.
  guardTabNavigation(event) {
    const tab = event.target?.closest?.(TAB_LINK_SELECTOR)
    if (!tab || !this.element.contains(tab)) return
    if (!this.#plainNavigation(event)) return
    if (this.dirtyFields().length === 0) return

    // The styled confirm is async, so the link's default navigation can't be
    // conditionally allowed: always stop it, then navigate ourselves once the
    // user accepts.
    event.preventDefault()

    // One prompt at a time: a second activation while the dialog is open (the
    // backdrop blocks the pointer, the keyboard can still reach the link)
    // neither stacks a second dialog nor navigates.
    if (this.#leaving) return
    this.#leaving = true

    const href = tab.getAttribute("href")

    showConfirmModal(UNSAVED_MESSAGE, {
      variant: "warning",
      title: UNSAVED_TITLE,
      confirmText: LEAVE_LABEL
    }).then((confirmed) => {
      this.#leaving = false
      if (!confirmed) return

      // Reset the edits first so the beforeunload guard has nothing left to
      // protect on the way out, then navigate once.
      this.discard()
      this.#navigate(href)
    })
  }

  dirtyFields() {
    return this.fieldTargets.filter((f) => this.#isDirty(f))
  }

  // ─── Private ──────────────────────────────────────────────────────────

  // Mirrors Turbo's own significance test: a modified or non-primary click
  // opens the tab in another tab/window and leaves this page's edits intact,
  // so it is never guarded; a click someone else already prevented is theirs.
  #plainNavigation(event) {
    if (event.defaultPrevented) return false
    if (typeof event.button === "number" && event.button !== 0) return false
    return !(event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
  }

  #navigate(href) {
    if (window.Turbo) {
      window.Turbo.visit(href)
    } else {
      window.location.assign(href)
    }
  }

  #valueOf(field) {
    if (field.type === "checkbox") return field.checked
    return field.value
  }

  #asBaselineString(field) {
    if (field.type === "checkbox") return String(field.checked)
    return field.value
  }

  #isDirty(field) {
    return this.#asBaselineString(field) !== field.dataset.baseline
  }

  #updateSaveBar() {
    const count = this.dirtyFields().length

    if (this.hasSaveBarTarget) {
      this.saveBarTarget.classList.toggle("is-visible", count > 0)
    }

    if (this.hasCountTarget) {
      this.countTarget.textContent = String(count)
    }
  }

  #setSaveBarButtonsDisabled(disabled) {
    if (!this.hasSaveBarTarget) return
    this.saveBarTarget.querySelectorAll("button").forEach((btn) => {
      btn.disabled = disabled
    })
  }

  #toast(message, variant) {
    if (typeof window.showToast === "function") {
      window.showToast(message, variant)
    }
  }

  // Bound reference for removeEventListener; the tab guard needs none — it is
  // a Stimulus action on the root element.
  #boundBeforeUnload = null
  #leaving = false
  #saving = false
}
